import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";
import PrivacyPage from './pages/PrivacyPage';

// Loading component for lazy-loaded routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="animate-spin h-8 w-8 text-primary" />
      <p className="text-sm text-slate-500">Loading...</p>
    </div>
  </div>
);

// Eagerly loaded pages (critical path)
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";

// Auth pages (lazy loaded)
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));

// Lazy-loaded pages for performance optimization
const HomePage = lazy(() => import("./pages/HomePage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const DestinationPage = lazy(() => import("./pages/DestinationPage"));
const PackagesPage = lazy(() => import("./pages/PackagesPage"));
const PackageDetailPage = lazy(() => import("./pages/PackageDetailPage"));
const OldPackageRedirect = lazy(() => import("./pages/OldPackageRedirect"));
const AgentProfilePage = lazy(() => import("./pages/AgentProfilePage"));
const InvoicePage = lazy(() => import("./pages/InvoicePage"));

// Admin pages (lazy loaded as they're less frequently accessed)
const AdminPackagesPage = lazy(() => import("./pages/admin/AdminPackagesPage"));
const AdminBookingsPage = lazy(() => import("./pages/admin/AdminBookingsPage"));
const AdminAgentsPage = lazy(() => import("./pages/admin/AdminAgentsPage"));
const AdminReportsPage = lazy(() => import("./pages/admin/AdminReportsPage"));
const AdminLocationsPage = lazy(() => import("./pages/admin/AdminLocationsPage"));
const AdminMediaPage = lazy(() => import("./pages/admin/AdminMediaPage"));
const AdminSettingsPage = lazy(() => import("./pages/admin/AdminSettingsPage"));
const AnalyticsDashboard = lazy(() => import("./pages/admin/AnalyticsDashboard"));
const StaffManagement = lazy(() => import("./pages/admin/StaffManagement"));
const SEOSettingsPage = lazy(() => import("./pages/admin/SEOSettingsPage"));
const SiteSettingsPage = lazy(() => import("./pages/admin/SiteSettingsPage"));

// CRM Pages (lazy loaded)
const CRMDashboard = lazy(() => import("./pages/crm/CRMDashboard"));
const CRMClients = lazy(() => import("./pages/crm/CRMClients"));
const CRMVouchers = lazy(() => import("./pages/crm/CRMVouchers"));
const CRMSales = lazy(() => import("./pages/crm/CRMSales"));
const CRMAgents = lazy(() => import("./pages/crm/CRMAgents"));
const CRMInvoices = lazy(() => import("./pages/crm/CRMInvoices"));
const CRMCalendar = lazy(() => import("./pages/crm/CRMCalendar"));

// Hotel Booking Pages (lazy loaded)
const HotelSearchPage = lazy(() => import("./pages/hotels/HotelSearchPage"));
const HotelDetailPage = lazy(() => import("./pages/hotels/HotelDetailPage"));
const HotelCitiesPage = lazy(() => import("./pages/admin/hotels/HotelCitiesPage"));
const HotelListPage = lazy(() => import("./pages/admin/hotels/HotelListPage"));
const RoomCategoriesPage = lazy(() => import("./pages/admin/hotels/RoomCategoriesPage"));
const HotelPricingPage = lazy(() => import("./pages/admin/hotels/HotelPricingPage"));
const HotelBookingsPage = lazy(() => import("./pages/admin/hotels/HotelBookingsPage"));
const HotelBulkUploadPage = lazy(() => import("./pages/admin/hotels/HotelBulkUploadPage"));
const HotelMediaPage = lazy(() => import("./pages/admin/hotels/HotelMediaPage"));
const AddCompleteHotelPage = lazy(() => import("./pages/admin/hotels/AddCompleteHotelPage"));
const HotelPriorityPage = lazy(() => import("./pages/admin/hotels/HotelPriorityPage"));
const HotelHomepageSettingsPage = lazy(() => import("./pages/admin/hotels/HotelHomepageSettingsPage"));
const HotelBookingReportsPage = lazy(() => import("./pages/admin/hotels/HotelBookingReportsPage"));
const HotelRateCalendarPage = lazy(() => import("./pages/admin/hotels/HotelRateCalendarPage"));
const HotelBulkPricingPage = lazy(() => import("./pages/admin/hotels/HotelBulkPricingPage"));
const BulkRateUploadPage = lazy(() => import("./pages/admin/hotels/BulkRateUploadPage"));
const HotelSpecialChargesPage = lazy(() => import("./pages/admin/hotels/HotelSpecialChargesPage"));
const HotelMasterPage = lazy(() => import("./pages/admin/hotels/HotelMasterPage"));
const UserAnalyticsDashboard = lazy(() => import("./pages/admin/analytics/UserAnalyticsDashboard"));
const AgentActivityDashboard = lazy(() => import("./pages/admin/analytics/AgentActivityDashboard"));
const AgentHotelsPage = lazy(() => import("./pages/agent/AgentHotelsPage"));
const AgentHotelBookingsPage = lazy(() => import("./pages/agent/AgentHotelBookingsPage"));

// Protected Route Component - supports admin and staff roles
const ProtectedRoute = ({ children, adminOnly = false, staffAllowed = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin-only routes
  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  // Routes that allow both admin and staff
  if (staffAllowed && !["admin", "staff"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirects logged-in users)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (user) {
    return <Navigate to="/packages" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Landing Page - Public */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        <Route path="/destinations/:slug" element={<DestinationPage />} />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
        
        {/* Protected Routes - Requires Login */}
        <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
        <Route path="/packages" element={<ProtectedRoute><PackagesPage /></ProtectedRoute>} />
        <Route path="/st_tour/:slug" element={<ProtectedRoute><PackageDetailPage /></ProtectedRoute>} />
        {/* 301 redirect: old /packages/:id → /st_tour/:slug */}
        <Route path="/packages/:id" element={<ProtectedRoute><OldPackageRedirect /></ProtectedRoute>} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><AgentProfilePage /></ProtectedRoute>} />
        <Route path="/invoice/:id" element={<ProtectedRoute><InvoicePage /></ProtectedRoute>} />
        
        {/* Admin Routes */}
        <Route path="/admin/packages" element={<ProtectedRoute adminOnly><AdminPackagesPage /></ProtectedRoute>} />
        <Route path="/admin/bookings" element={<ProtectedRoute adminOnly><AdminBookingsPage /></ProtectedRoute>} />
        <Route path="/admin/agents" element={<ProtectedRoute adminOnly><AdminAgentsPage /></ProtectedRoute>} />
        <Route path="/admin/reports" element={<ProtectedRoute adminOnly><AdminReportsPage /></ProtectedRoute>} />
        <Route path="/admin/locations" element={<ProtectedRoute adminOnly><AdminLocationsPage /></ProtectedRoute>} />
        <Route path="/admin/media" element={<ProtectedRoute adminOnly><AdminMediaPage /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AdminSettingsPage /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute staffAllowed><AnalyticsDashboard /></ProtectedRoute>} />
        <Route path="/admin/staff" element={<ProtectedRoute adminOnly><StaffManagement /></ProtectedRoute>} />
        <Route path="/admin/seo" element={<ProtectedRoute adminOnly><SEOSettingsPage /></ProtectedRoute>} />
        <Route path="/admin/site-settings" element={<ProtectedRoute adminOnly><SiteSettingsPage /></ProtectedRoute>} />

        {/* CRM Routes - Allow staff access to most CRM features */}
        <Route path="/admin/crm/dashboard" element={<ProtectedRoute staffAllowed><CRMDashboard /></ProtectedRoute>} />
        <Route path="/admin/crm/clients" element={<ProtectedRoute staffAllowed><CRMClients /></ProtectedRoute>} />
        <Route path="/admin/crm/vouchers" element={<ProtectedRoute staffAllowed><CRMVouchers /></ProtectedRoute>} />
        <Route path="/admin/crm/sales" element={<ProtectedRoute staffAllowed><CRMSales /></ProtectedRoute>} />
        <Route path="/admin/crm/agents" element={<ProtectedRoute staffAllowed><CRMAgents /></ProtectedRoute>} />
        <Route path="/admin/crm/invoices" element={<ProtectedRoute staffAllowed><CRMInvoices /></ProtectedRoute>} />
        <Route path="/admin/crm/calendar" element={<ProtectedRoute staffAllowed><CRMCalendar /></ProtectedRoute>} />

        {/* Hotel Booking Routes - Customer Facing */}
        <Route path="/hotels" element={<ProtectedRoute><HotelSearchPage /></ProtectedRoute>} />
        <Route path="/hotels/:hotelId" element={<ProtectedRoute><HotelDetailPage /></ProtectedRoute>} />

        {/* Agent Hotel Routes */}
        <Route path="/agent/hotels" element={<ProtectedRoute><AgentHotelsPage /></ProtectedRoute>} />
        <Route path="/agent/hotel-bookings" element={<ProtectedRoute><AgentHotelBookingsPage /></ProtectedRoute>} />

        {/* Hotel Admin Routes */}
        <Route path="/admin/hotels/cities" element={<ProtectedRoute adminOnly><HotelCitiesPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/list" element={<ProtectedRoute adminOnly><HotelListPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/add-complete" element={<ProtectedRoute adminOnly><AddCompleteHotelPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/rooms" element={<ProtectedRoute adminOnly><RoomCategoriesPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/pricing" element={<ProtectedRoute adminOnly><HotelPricingPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/priority" element={<ProtectedRoute adminOnly><HotelPriorityPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/media" element={<ProtectedRoute adminOnly><HotelMediaPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/bulk-upload" element={<ProtectedRoute adminOnly><HotelBulkUploadPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/bulk-rate-upload" element={<ProtectedRoute adminOnly><BulkRateUploadPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/bookings" element={<ProtectedRoute staffAllowed><HotelBookingsPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/homepage-settings" element={<ProtectedRoute adminOnly><HotelHomepageSettingsPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/reports" element={<ProtectedRoute staffAllowed><HotelBookingReportsPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/rate-calendar" element={<ProtectedRoute adminOnly><HotelRateCalendarPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/bulk-pricing" element={<ProtectedRoute adminOnly><HotelBulkPricingPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/special-charges" element={<ProtectedRoute adminOnly><HotelSpecialChargesPage /></ProtectedRoute>} />
        <Route path="/admin/hotels/master" element={<ProtectedRoute adminOnly><HotelMasterPage /></ProtectedRoute>} />
        
        {/* Analytics */}
        <Route path="/admin/analytics/user-activity" element={<ProtectedRoute staffAllowed><UserAnalyticsDashboard /></ProtectedRoute>} />
        <Route path="/admin/user-analytics" element={<ProtectedRoute adminOnly><AgentActivityDashboard /></ProtectedRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
